import { tokens } from '@/design';
import type { CSSProperties } from 'react';
import {
  DEFAULT_LAYOUT_TEXT_SYMBOL,
  normalizeLegendElement,
} from './layoutDocument';
import { legendStyleMeta } from './legendStyles';
import type {
  LayoutTextSymbol,
  LegendElement,
  LegendItemArrangement,
  LegendItemOverride,
  LegendLayerItem,
  LegendTextPart,
} from './layoutTypes';

export interface LayoutLegendProps {
  readonly legend: LegendElement;
  readonly items: readonly LegendLayerItem[];
}

function shownText(value: string, symbol: LayoutTextSymbol): string {
  if (symbol.textCase === 'uppercase' || symbol.textCase === 'smallCaps') {
    return value.toUpperCase();
  }
  if (symbol.textCase === 'lowercase') return value.toLowerCase();
  return value;
}

function textStyle(
  symbol: LayoutTextSymbol,
  fontScale = 1,
): CSSProperties {
  const bold = symbol.fontStyle === 'bold' || symbol.fontStyle === 'boldItalic';
  const italic =
    symbol.fontStyle === 'italic' || symbol.fontStyle === 'boldItalic';
  const shadows: string[] = [];
  if (symbol.halo.enabled) {
    const size = symbol.halo.sizePt;
    shadows.push(
      `${size}px 0 ${symbol.halo.color}`,
      `${-size}px 0 ${symbol.halo.color}`,
      `0 ${size}px ${symbol.halo.color}`,
      `0 ${-size}px ${symbol.halo.color}`,
    );
  }
  if (symbol.shadow.enabled) {
    shadows.push(
      `${symbol.shadow.offsetXPt}px ${symbol.shadow.offsetYPt}px ${symbol.shadow.color}`,
    );
  }
  return {
    position: 'relative',
    display: 'inline-block',
    maxWidth: '100%',
    color: symbol.color,
    fontFamily: symbol.fontFamily,
    fontSize: `${Math.max(1, symbol.sizePt * fontScale)}pt`,
    fontWeight: bold ? tokens.font.weight.bold : tokens.font.weight.regular,
    fontStyle: italic ? 'italic' : 'normal',
    fontVariant: symbol.textCase === 'smallCaps' ? 'small-caps' : undefined,
    textDecoration: [
      symbol.underline ? 'underline' : '',
      symbol.strikethrough ? 'line-through' : '',
    ].filter(Boolean).join(' ') || undefined,
    textAlign:
      symbol.horizontalAlignment === 'justify'
        ? 'justify'
        : symbol.horizontalAlignment,
    verticalAlign:
      symbol.positionAdjustment === 'superscript'
        ? 'super'
        : symbol.positionAdjustment === 'subscript'
          ? 'sub'
          : symbol.verticalAlignment === 'middle'
            ? 'middle'
            : symbol.verticalAlignment === 'top'
              ? 'top'
              : symbol.verticalAlignment === 'bottom'
                ? 'bottom'
                : 'baseline',
    transform: `translate(${symbol.offsetXPt}pt, ${symbol.offsetYPt}pt) rotate(${symbol.angle}deg)`,
    transformOrigin: 'left center',
    opacity: symbol.opacityPercent / 100,
    textShadow: shadows.length > 0 ? shadows.join(', ') : undefined,
    WebkitTextStroke:
      symbol.outlineWidthPt > 0
        ? `${symbol.outlineWidthPt}pt ${symbol.outlineColor}`
        : undefined,
    background: symbol.callout.enabled ? symbol.callout.fillColor : undefined,
    outline: symbol.callout.enabled
      ? `${tokens.border.width.thin}px solid ${symbol.callout.outlineColor}`
      : undefined,
    padding: symbol.callout.enabled ? `${symbol.callout.paddingPt}pt` : undefined,
  };
}

function symbolFor(
  legend: LegendElement,
  part: LegendTextPart,
): LayoutTextSymbol {
  const meta = legendStyleMeta(legend.styleId ?? 'centerTitle');
  const size =
    part === 'title'
      ? meta.titleSizePt
      : part === 'description'
        ? Math.max(6, meta.textSizePt - 1)
        : meta.textSizePt;
  return {
    ...DEFAULT_LAYOUT_TEXT_SYMBOL,
    ...(legend.textSymbols?.[part] ?? {}),
    sizePt: legend.textSymbols?.[part]?.sizePt ?? size,
    fontStyle: legend.textSymbols?.[part]?.fontStyle ?? (
      part === 'title'
      || part === 'groupLayerName'
      || part === 'layerName'
      || part === 'heading'
        ? 'bold'
        : 'regular'
    ),
    halo: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL.halo,
      ...legend.textSymbols?.[part]?.halo,
    },
    shadow: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL.shadow,
      ...legend.textSymbols?.[part]?.shadow,
    },
    callout: {
      ...DEFAULT_LAYOUT_TEXT_SYMBOL.callout,
      ...legend.textSymbols?.[part]?.callout,
    },
  };
}

function TextPart({
  legend,
  part,
  value,
  fontScale,
  wrap,
  wrapWidthPt,
}: {
  readonly legend: LegendElement;
  readonly part: LegendTextPart;
  readonly value: string;
  readonly fontScale: number;
  readonly wrap?: boolean;
  readonly wrapWidthPt?: number;
}) {
  const symbol = symbolFor(legend, part);
  return (
    <span
      data-legend-text-part={part}
      style={{
        ...textStyle(symbol, fontScale),
        whiteSpace: wrap ? 'normal' : 'nowrap',
        overflowWrap: wrap ? 'anywhere' : undefined,
        maxWidth: wrap && wrapWidthPt ? `${wrapWidthPt}pt` : '100%',
      }}
    >
      {shownText(value, symbol)}
    </span>
  );
}

function RasterPatch({
  item,
  override,
  widthPt,
  heightPt,
}: {
  readonly item: LegendLayerItem;
  readonly override: LegendItemOverride;
  readonly widthPt: number;
  readonly heightPt: number;
}) {
  const ramp = item.rasterRamp;
  if (!ramp) return null;
  const type = override.type ?? 'default';
  const horizontal = type === 'horizontal';
  const direction = horizontal
    ? override.reverseRamp ? 'to left' : 'to right'
    : override.reverseRamp ? 'to bottom' : 'to top';
  const gradient = ramp.cssGradient.replace(
    /linear-gradient\([^,]+,/,
    `linear-gradient(${direction},`,
  );
  return (
    <span
      role="img"
      aria-label={`Color ramp from ${override.minLabel ?? ramp.minLabel} to ${override.maxLabel ?? ramp.maxLabel}`}
      data-legend-ramp-type={type}
      style={{
        display: 'inline-grid',
        gridTemplateColumns: horizontal ? '1fr' : 'auto auto',
        gridTemplateRows: horizontal ? 'auto auto' : '1fr 1fr',
        alignItems: 'center',
        gap: tokens.space[1],
        width: `${widthPt}pt`,
        minHeight: `${heightPt}pt`,
      }}
    >
      <span
        style={{
          gridRow: horizontal ? '1' : '1 / 3',
          gridColumn: horizontal ? '1' : '1',
          width: horizontal ? `${widthPt}pt` : `${Math.min(widthPt, 16)}pt`,
          height: horizontal ? `${Math.min(heightPt, 10)}pt` : `${heightPt}pt`,
          background: gradient,
          border: tokens.border.thin,
        }}
      />
      <span
        style={{
          gridRow: horizontal ? '2' : '1',
          gridColumn: horizontal ? '1' : '2',
          alignSelf: 'start',
          fontSize: tokens.font.size.xs,
        }}
      >
        {override.maxLabel ?? ramp.maxLabel}
      </span>
      <span
        style={{
          gridRow: horizontal ? '2' : '2',
          gridColumn: horizontal ? '1' : '2',
          justifySelf: horizontal ? 'end' : undefined,
          alignSelf: 'end',
          fontSize: tokens.font.size.xs,
        }}
      >
        {override.minLabel ?? ramp.minLabel}
      </span>
    </span>
  );
}

function VectorPatch({
  item,
  widthPt,
  heightPt,
}: {
  readonly item: LegendLayerItem;
  readonly widthPt: number;
  readonly heightPt: number;
}) {
  const point = /point/i.test(item.kind);
  const line = /line/i.test(item.kind);
  return (
    <span
      aria-hidden
      data-legend-vector-patch={item.kind}
      style={{
        display: 'inline-block',
        width: `${widthPt}pt`,
        height: `${heightPt}pt`,
        flex: '0 0 auto',
        boxSizing: 'border-box',
        background: line ? 'transparent' : item.patchColor ?? tokens.color.accent.strong,
        border: line
          ? 0
          : `${tokens.border.width.thin}px solid ${tokens.color.ink.inverse}`,
        borderRadius: point ? tokens.radius.pill : 0,
        borderTop: line
          ? `${Math.max(1, heightPt / 5)}pt solid ${item.patchColor ?? tokens.color.accent.strong}`
          : undefined,
      }}
    />
  );
}

function arrangementParts(
  arrangement: LegendItemArrangement,
): readonly ('patch' | 'label' | 'description')[] {
  const parts: Readonly<
    Record<
      LegendItemArrangement,
      readonly ('patch' | 'label' | 'description')[]
    >
  > = {
    patchLabelDescription: ['patch', 'label', 'description'],
    patchDescriptionLabel: ['patch', 'description', 'label'],
    labelPatchDescription: ['label', 'patch', 'description'],
    labelDescriptionPatch: ['label', 'description', 'patch'],
    descriptionPatchLabel: ['description', 'patch', 'label'],
    descriptionLabelPatch: ['description', 'label', 'patch'],
  };
  return parts[arrangement];
}

function LegendItem({
  legend,
  item,
  override,
  fontScale,
}: {
  readonly legend: LegendElement;
  readonly item: LegendLayerItem;
  readonly override: LegendItemOverride;
  readonly fontScale: number;
}) {
  const style = legendStyleMeta(legend.styleId ?? 'centerTitle');
  if (override.visible === false) return null;
  const widthPt = override.patchWidthPt ?? 24 * style.patchScale;
  const heightPt = override.patchHeightPt ?? 12 * style.patchScale;
  const showLayerName = override.showLayerName ?? style.showLayerName;
  const showHeading = override.showHeading ?? style.showHeading;
  const showLabel = override.showLabel !== false;
  const showDescription = override.showDescription === true && Boolean(item.description);
  const arrangement = override.arrangement ?? 'patchLabelDescription';
  const patch = item.rasterRamp ? (
    <RasterPatch
      item={item}
      override={override}
      widthPt={widthPt}
      heightPt={heightPt}
    />
  ) : (
    <VectorPatch item={item} widthPt={widthPt} heightPt={heightPt} />
  );
  const parts = arrangementParts(arrangement);
  const label = override.label ?? item.label;

  return (
    <div
      data-legend-item={item.id}
      style={{
        breakInside: 'avoid',
        minWidth: 0,
        paddingBottom: `${legend.spacing?.classGapPt ?? 2}pt`,
      }}
    >
      {override.showGroupLayerName !== false && item.groupLayerName ? (
        <div style={{ marginBottom: `${legend.spacing?.groupLayerNameGapPt ?? 2}pt` }}>
          <TextPart
            legend={legend}
            part="groupLayerName"
            value={item.groupLayerName}
            fontScale={fontScale}
          />
        </div>
      ) : null}
      {showLayerName ? (
        <div
          style={{
            marginLeft: `${override.layerNameIndentPt ?? 2}pt`,
            marginBottom: `${legend.spacing?.layerNameGapPt ?? 2}pt`,
          }}
        >
          <TextPart
            legend={legend}
            part="layerName"
            value={label}
            fontScale={fontScale}
            wrap={legend.wrapLabels}
            wrapWidthPt={legend.labelWrapWidthPt}
          />
        </div>
      ) : null}
      {showHeading && item.heading ? (
        <div
          style={{
            marginLeft: `${override.headingIndentPt ?? 0}pt`,
            marginBottom: `${legend.spacing?.headingGapPt ?? 2}pt`,
          }}
        >
          <TextPart
            legend={legend}
            part="heading"
            value={item.heading}
            fontScale={fontScale}
          />
        </div>
      ) : null}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: `${legend.spacing?.patchLabelGapPt ?? 4}pt`,
          marginLeft: `${override.classIndentPt ?? 0}pt`,
        }}
      >
        {parts.map((part) => {
          if (part === 'patch') return <span key={part}>{patch}</span>;
          if (part === 'label' && showLabel) {
            return (
              <TextPart
                key={part}
                legend={legend}
                part="label"
                value={`${label}${
                  override.showFeatureCount && item.featureCount != null
                    ? ` (${item.featureCount})`
                    : ''
                }`}
                fontScale={fontScale}
                wrap={legend.wrapLabels}
                wrapWidthPt={legend.labelWrapWidthPt}
              />
            );
          }
          if (part === 'description' && showDescription && item.description) {
            return (
              <TextPart
                key={part}
                legend={legend}
                part="description"
                value={item.description}
                fontScale={fontScale}
                wrap={legend.wrapDescriptions}
                wrapWidthPt={legend.descriptionWrapWidthPt}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

export function LayoutLegend({ legend: sourceLegend, items }: LayoutLegendProps) {
  const legend = normalizeLegendElement(sourceLegend);
  const style = legendStyleMeta(legend.styleId ?? 'centerTitle');
  const visibleItems = items.filter(
    (item) => legend.itemOverrides?.[item.id]?.visible !== false,
  );
  const fitting = legend.fittingStrategy ?? style.fittingStrategy;
  const columns =
    fitting === 'adjustColumns' || fitting === 'adjustColumnsAndFont'
      ? Math.max(1, Math.min(4, Math.ceil(visibleItems.length / 4)))
      : Math.max(1, legend.columnCount ?? style.columnCount);
  const fontScale =
    fitting === 'adjustFontSize' || fitting === 'adjustColumnsAndFont'
      ? Math.max(
          (legend.minimumFontSizePt ?? 6) / style.textSizePt,
          1 - Math.max(0, visibleItems.length - columns * 4) * 0.06,
        )
      : 1;
  const capacity = fitting === 'adjustFrame' ? Number.POSITIVE_INFINITY : columns * 7;
  const drawnItems = visibleItems.slice(0, capacity);
  const overflowing = drawnItems.length < visibleItems.length;
  const background = legend.background ?? {
    enabled: style.background.enabled,
    color: style.background.color,
  };
  const border = legend.border ?? {
    enabled: style.border.enabled,
    color: style.border.color,
    outlineWidthPt: 1,
  };
  const shadow = legend.shadow;

  if (legend.visible === false) return null;
  return (
    <div
      data-legend-style={legend.styleId ?? 'centerTitle'}
      data-legend-columns={columns}
      data-legend-overflowing={overflowing}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: fitting === 'adjustFrame' ? 'auto' : 'hidden',
        boxSizing: 'border-box',
        padding: `${Math.max(
          background.gapYPt ?? 4,
          border.gapYPt ?? 4,
        )}pt ${Math.max(
          background.gapXPt ?? 4,
          border.gapXPt ?? 4,
        )}pt`,
        background: background.enabled ? background.color : 'transparent',
        color: tokens.color.ink.inverse,
        border: border.enabled
          ? `${border.outlineWidthPt ?? 1}pt solid ${border.color}`
          : 'none',
        boxShadow: shadow?.enabled
          ? `${shadow.offsetXPt}pt ${shadow.offsetYPt}pt ${shadow.gapXPt}pt ${shadow.color}`
          : 'none',
        fontFamily: tokens.font.family.ui,
      }}
    >
      {legend.showTitle !== false ? (
        <div
          style={{
            marginBottom: `${legend.spacing?.titleGapPt ?? 5}pt`,
            textAlign: symbolFor(legend, 'title').horizontalAlignment,
          }}
        >
          <TextPart
            legend={legend}
            part="title"
            value={legend.title ?? 'Legend'}
            fontScale={fontScale}
            wrap={legend.wrapLabels}
            wrapWidthPt={legend.labelWrapWidthPt * columns}
          />
        </div>
      ) : null}
      {drawnItems.length === 0 ? (
        <div style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
          No visible layers
        </div>
      ) : (
        <div
          role="list"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, ${
              legend.equalColumnWidths ? 'minmax(0, 1fr)' : 'max-content'
            })`,
            alignItems: 'start',
            gap: `${legend.spacing?.columnGapPt ?? 12}pt`,
          }}
        >
          {drawnItems.map((item) => (
            <div
              key={item.id}
              role="listitem"
              style={{
                gridColumn:
                  fitting === 'manualColumns'
                    ? Math.min(
                        columns,
                        Math.max(1, legend.itemOverrides?.[item.id]?.manualColumn ?? 1),
                      )
                    : undefined,
              }}
            >
              <LegendItem
                legend={legend}
                item={item}
                override={legend.itemOverrides?.[item.id] ?? {}}
                fontScale={fontScale}
              />
            </div>
          ))}
        </div>
      )}
      {overflowing ? (
        <span
          role="status"
          aria-label="Legend contents overflow"
          title="Some legend items do not fit"
          style={{
            position: 'absolute',
            right: tokens.space[1],
            bottom: tokens.space[1],
            color: tokens.color.semantic.danger,
            fontWeight: tokens.font.weight.bold,
          }}
        >
          …
        </span>
      ) : null}
    </div>
  );
}
